import React from 'react';
import PanelHeading from './PanelHeading';
import { fmtCost, fmtDurationHM, fmtNumber } from './vendor';

/**
 * Range summary — a person's time beside their agents' spend.
 *
 * `totals` { hours, entries, clients } and `agent_totals` { cost, entries } come
 * back from fn_my_timesheet as two SEPARATE objects, and they stay separate here.
 * Hours plus dollars is a number that means nothing: they are never summed, never
 * share an axis, and never collapse into a single figure. Showing both side by
 * side IS the page's argument — the substitution curve, per person, for this
 * range (05_EFFORT §1).
 *
 * A null agent cost renders `—`, never `$0.00`: rates are unset on most people
 * and zero is a claim we cannot make.
 *
 * @param {{hours:number, entries:number, clients:number}} [props.totals]
 * @param {{cost:number|null, entries:number}} [props.agentTotals]
 */
const CARD = 'bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-4 shadow-sm';
const LABEL = 'text-xs font-semibold uppercase tracking-wide';
const FIGURE = 'mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-100';
const SUB = 'mt-1 text-sm text-gray-500 dark:text-gray-400';

// totals.hours is decimal hours. fmtDurationHM speaks seconds and renders a bare
// 0 as "0s", which reads wrong for a whole range — so zero is spelled "0h".
const hoursLabel = (hours) => {
  if (hours == null) return '—';
  if (!Number(hours)) return '0h';
  return fmtDurationHM(Math.round(Number(hours) * 3600));
};

const plural = (n, one, many) => `${fmtNumber(n)} ${Number(n) === 1 ? one : many}`;

export default function EffortTotals({ totals, agentTotals }) {
  const hours = totals?.hours ?? null;
  const cost = agentTotals?.cost ?? null;
  const agentEntries = agentTotals?.entries ?? 0;

  // Nothing logged and no agent runs — the empty worklist already says so, and a
  // row of zeroes above it would only add noise to a correct empty state. The
  // cost is deliberately NOT part of this test: with no runs a cost figure means
  // nothing, whether the server sends 0 or null.
  if (!Number(hours) && !Number(agentEntries)) return null;

  return (
    <div className={CARD} data-testid="effort-totals">
      <PanelHeading
        title="Range summary"
        description="Your logged hours beside your agents' spend — shown side by side, never added together."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className={`${LABEL} text-gray-500 dark:text-gray-400`}>You</p>
          <p className={FIGURE} data-testid="totals-hours">{hoursLabel(hours)}</p>
          <p className={SUB}>
            {plural(totals?.entries ?? 0, 'entry', 'entries')} · {plural(totals?.clients ?? 0, 'client', 'clients')}
          </p>
        </div>

        {/* Divider on sm+ keeps the two figures visibly separate within the one
            card; the violet label carries the "agents" identity. */}
        <div className="sm:border-l sm:border-gray-200 dark:sm:border-gray-800 sm:pl-4">
          <p className={`${LABEL} text-violet-700 dark:text-violet-300`}>Your agents</p>
          <p className={FIGURE} data-testid="totals-agent-cost">{fmtCost(cost)}</p>
          <p className={SUB}>{plural(agentEntries, 'run', 'runs')}</p>
        </div>
      </div>
    </div>
  );
}
